import { ListChecks, Lock, type LucideIcon, Shield, Zap } from "lucide-react";
import type { ReactNode } from "react";

import type { EnemyProfile } from "../api/types";
import { Switch } from "../components/Checkbox";
import { ChoiceCard } from "../components/panel";
import { Motion, Presence, useLastDefined } from "../components/presence";
import { t } from "../i18n/i18n";
import { DEFAULT_PROFILE, PROFILES, profileEffects, profileMotto, profileName } from "./enemyProfile";
import { onRadioGroupKeyDown, rovingTabIndex } from "./radioGroupKeys";

/** Symbole de chaque profil (demande d'Alexis du 28/10/2026) : méthodique, la procédure suivie point par point ;
 *  audacieux, l'éclair ; prudent, le bouclier ; rigide, le cadenas. */
const PROFILE_ICONS: Record<EnemyProfile, LucideIcon> = {
  methodique: ListChecks,
  audacieux: Zap,
  prudent: Shield,
  rigide: Lock,
};

/** Réglage « Profil du chef ENI » des Données initiales (registre D18) : interrupteur éteint par défaut ; allumé,
 *  quatre cartes de choix (méthodique par défaut) et, dessous, ce que le profil choisi change dans le moteur, tiré de
 *  la table unique des paramètres. Tout apparaît en fondu. */
export function EnemyProfileField({
  value,
  onChange,
  conflict,
}: {
  value: EnemyProfile | null;
  onChange: (value: EnemyProfile | null) => void;
  conflict?: ReactNode;
}) {
  const on = value !== null;
  // Profil affiché pendant la sortie : les cartes restent lisibles le temps de leur fondu.
  const shown = useLastDefined(value) ?? DEFAULT_PROFILE;
  return (
    <div data-attention="dossier-enemy-profile" className="rounded-lg">
      <Switch checked={on} onChange={(next) => onChange(next ? DEFAULT_PROFILE : null)} label={t("Profil du chef ENI")} />
      <p className="mt-1 text-meta leading-snug text-ink-muted">
        {t("Facultatif : le même dossier donne des scénarios différents selon la manière de commander de l'ennemi.")}
      </p>
      {conflict}
      <Presence show={on}>
        <Motion origin="fade" className="mt-2.5 space-y-2.5">
          <div
            role="radiogroup"
            aria-label={t("Profil du chef ENI")}
            className="grid grid-cols-2 gap-1.5"
            onKeyDown={(e) => onRadioGroupKeyDown(e, (i) => onChange(PROFILES[i] ?? DEFAULT_PROFILE))}
          >
            {PROFILES.map((key, i) => (
              <ChoiceCard
                key={key}
                radio
                className="h-full"
                selected={shown === key}
                tabIndex={rovingTabIndex(i, PROFILES.indexOf(shown), PROFILES.map(() => true))}
                onClick={() => onChange(key)}
                icon={PROFILE_ICONS[key]}
                title={profileName(key)}
                detail={profileMotto(key)}
              />
            ))}
          </div>
          {/* Effets du profil choisi : le bloc passe en fondu d'un profil à l'autre. */}
          <Motion key={shown} origin="fade" className="rounded-lg border border-line bg-canvas px-3 py-2.5" data-testid="enemy-profile-effects">
            <dl className="space-y-2 text-ui leading-snug">
              {profileEffects(shown).map((effect) => (
                <div key={effect.label} className="grid grid-cols-[7.5rem_1fr] gap-x-3 max-sm:grid-cols-1">
                  <dt className="font-medium text-ink-soft">{effect.label}</dt>
                  <dd className="text-ink">{effect.text}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-2.5 text-meta leading-snug text-ink-muted">
              {t("Ces effets sont des hypothèses d'exercice, non des valeurs doctrinales.")}
            </p>
          </Motion>
        </Motion>
      </Presence>
    </div>
  );
}
